import type { EstablishmentOption, GeneratorOption } from "@/integrations/generatorApi/client";

interface ProfileGeneratorFields {
  is_internal: boolean;
  generator_id: number | null;
  /** Nome vindo da tabela legada `generators` do Supabase (useUnifiedAuth). */
  generator_name: string | null;
}

/** Recorte do `establishmentAccess` do Redux que a resolução do nome usa. */
export interface GeneratorAccessForAnalytics {
  /** Árvore visível ao usuário, raiz incluída (`GET /generators/me` → `establishments`). */
  establishments: EstablishmentOption[];
  /** Só as raízes (N1). */
  generators: GeneratorOption[];
  fetchedAt: number | null;
  error: string | null;
}

/**
 * Nome do gerador para a user property `generator_name` do GA4.
 *
 * Fonte principal: `GET /generators/me` (os mesmos geradores dos filtros, no Redux), casando pelo
 * `id` numérico — `external_id` ali é UUID, nunca o `generator_id` do perfil. A árvore vem
 * primeiro porque o usuário pode ser vinculado a uma unidade filha (N2+), que não está entre as
 * raízes. O Supabase fica por último: a tabela legada não tem os geradores criados depois da
 * migração. Usuário interno Musa não tem gerador — fica sem nome.
 */
export const resolveAnalyticsGeneratorName = (
  profile: ProfileGeneratorFields,
  access: Pick<GeneratorAccessForAnalytics, "establishments" | "generators">,
): string | null => {
  if (profile.is_internal) return null;

  const id = profile.generator_id != null ? String(profile.generator_id) : null;
  const fromApi = id
    ? (access.establishments.find((e) => e.id === id)?.name || access.generators.find((g) => g.id === id)?.name)
    : undefined;

  return fromApi || profile.generator_name || null;
};

/**
 * A identidade do GA4 (user_id + user properties) só é enviada quando o nome do gerador já pode
 * ser resolvido: senão os primeiros eventos da sessão saem com `generator_name = (not set)`, e o
 * GA não preenche eventos anteriores. Interno e usuário sem gerador não esperam nada; o externo
 * espera a lista de geradores carregar ou falhar (a falha cai no fallback do Supabase).
 */
export const isGeneratorListSettled = (
  profile: Pick<ProfileGeneratorFields, "is_internal" | "generator_id">,
  access: Pick<GeneratorAccessForAnalytics, "fetchedAt" | "error">,
): boolean =>
  profile.is_internal || profile.generator_id == null || access.fetchedAt != null || access.error != null;
